import {
  PokemonType,
  SkillData,
  SkillEvent_MeleeHit,
  SkillEvent_Vfx,
  SkillEventCheck,
  SkillMomentum,
  SkillRangeType,
  SkillType,
  StatusEffect,
} from "types/battle-engine/battle";
import { parseSkillIdToName } from "../utils/parse-id-to-name";
import { msToTicks } from "../utils/ms-to-ticks";

// Simple Melee Skills - 1 per PokeType
// Basic melee attack
export const [
  TACKLE,
  FIRE_PUNCH,
  AQUA_TAIL,
  BRANCH_POKE,
  THUNDER_PUNCH,
  ICE_PUNCH,
  KARATE_CHOP,
  POISON_JAB,
  MUD_SLAP,
  PECK,
  ZEN_HEADBUTT,
  POUNCE,
  ROCK_SMASH,
  LICK,
  DRAGON_CLAW,
  BITE,
  METAL_CLAW,
  PLAY_ROUGH,
  SCRATCH,
  HEADBUTT,
] = [
  {
    id: "tackle",
    type: PokemonType.NORMAL,
    impact_sprite: "melee-1-white",
    description: "A full-body charge that tackles the enemy.",
  },
  {
    id: "fire-punch",
    type: PokemonType.FIRE,
    impact_sprite: "melee-1-red",
    description: "A fiery punch that hits the target.",
  },
  {
    id: "aqua-tail",
    type: PokemonType.WATER,
    impact_sprite: "melee-1-blue",
    description: "A powerful tail swipe infused with water.",
  },
  {
    id: "branch-poke",
    type: PokemonType.GRASS,
    impact_sprite: "melee-1-green",
    description: "A sharp branch jabs at the enemy.",
  },
  {
    id: "thunder-punch",
    type: PokemonType.ELECTRIC,
    impact_sprite: "melee-1-yellow",
    description: "An electrified punch that hits the target.",
  },
  {
    id: "ice-punch",
    type: PokemonType.ICE,
    impact_sprite: "melee-1-blue",
    description: "A freezing punch that chills the enemy.",
  },
  {
    id: "karate-chop",
    type: PokemonType.FIGHTING,
    impact_sprite: "melee-1-red",
    description: "A precise karate chop strike.",
  },
  {
    id: "poison-jab",
    type: PokemonType.POISON,
    impact_sprite: "melee-1-pink",
    description: "A poisonous jab that hits the target.",
  },
  {
    id: "mud-slap",
    type: PokemonType.GROUND,
    impact_sprite: "melee-1-orange",
    description: "Mud is slapped onto the enemy.",
  },
  {
    id: "peck",
    type: PokemonType.FLYING,
    impact_sprite: "melee-1-white",
    description: "A quick pecking attack with a beak.",
  },
  {
    id: "zen-headbutt",
    type: PokemonType.PSYCHIC,
    impact_sprite: "melee-1-pink",
    description: "A headbutt infused with psychic energy.",
  },
  {
    id: "pounce",
    type: PokemonType.BUG,
    impact_sprite: "melee-1-green",
    description: "A leaping pounce that surprises the enemy.",
  },
  {
    id: "rock-smash",
    type: PokemonType.ROCK,
    impact_sprite: "melee-1-white",
    description: "A powerful smash that hits the target.",
  },
  {
    id: "lick",
    type: PokemonType.GHOST,
    impact_sprite: "melee-1-pink",
    description: "A ghostly tongue licks the enemy.",
  },
  {
    id: "dragon-claw",
    power: 6,
    type: PokemonType.DRAGON,
    impact_sprite: "melee-1-blue",
    multihit: 2,
    description: "Sharp dragon claws slash at the target twice.",
    cast_sprites: [
      {
        duration_ticks: 25,
        target_type: "enemy",
        sprite_path: "/assets/spells/claws/fanfx2_claw_large_blue.png",
        sprite_scale: 1,
        speed: 0.75,
        blocks_event_pipeline: false,
      },
      {
        duration_ticks: 35,
        target_type: "enemy",
        sprite_path: "/assets/spells/claws/fx2_claw_large_black.png",
        sprite_scale: 1.25,
        speed: 0.65,
        blocks_event_pipeline: true,
      },
    ],
  },
  {
    id: "bite",
    type: PokemonType.DARK,
    impact_sprite: "melee-1-white",
    description: "A vicious bite that hits the target.",
  },
  {
    id: "metal-claw",
    power: 6,
    type: PokemonType.STEEL,
    impact_sprite: "melee-1-white",
    multihit: 2,
    description: "Sharp metal claws slash at the target twice.",
    cast_sprites: [
      {
        duration_ticks: 25,
        target_type: "enemy",
        sprite_path: "/assets/spells/claws/fanfx2_claw_large_white.png",
        sprite_scale: 1,
        speed: 0.75,
        blocks_event_pipeline: false,
      },
      {
        duration_ticks: 35,
        target_type: "enemy",
        sprite_path: "/assets/spells/claws/fx2_claw_large_white.png",
        sprite_scale: 1.25,
        speed: 0.65,
        blocks_event_pipeline: true,
      },
    ],
  },
  {
    id: "play-rough",
    type: PokemonType.FAIRY,
    impact_sprite: "melee-1-pink",
    description: "A playful but rough attack.",
  },
  // More simple melee
  {
    id: "scratch",
    type: PokemonType.NORMAL,
    impact_sprite: "melee-1-white",
    power: 5,
    cooldown: 65,
    description: "Scratches the opponent, dealing damage.",
  },
  {
    id: "headbutt",
    type: PokemonType.NORMAL,
    impact_sprite: "melee-1-white",
    power: 15,
    cooldown: 250,
    description: "A strong headbutt.",
  },
].map((skill) => ({
  id: skill.id,
  event_checks:
    "event_checks" in skill
      ? (skill.event_checks as SkillData["event_checks"])
      : undefined,
  momentum:
    "momentum" in skill ? (skill.momentum as SkillData["momentum"]) : undefined,
  team_conditions:
    "team_conditions" in skill
      ? (skill.team_conditions as SkillData["team_conditions"])
      : undefined,
  starts_active:
    "starts_active" in skill ? (skill.starts_active as boolean) : undefined,
  name: parseSkillIdToName(skill.id),
  description: skill.description,
  power: skill.power ?? 8,
  pokemon_type: skill.type,
  types: [SkillType.DAMAGE],
  cooldown_ticks: skill.cooldown ?? 150,
  mana_cost: 5,
  range: {
    type: SkillRangeType.POINT_TARGET,
    value: 1,
  },
  on_hit: [
    {
      __typename: "Vfx",
      duration_ticks: 1,
      target: {
        type: "enemy",
        id: 0,
      },
      status_effects: [],
      starting_tick: 0,
      sprite_path: `/assets/spells/impact/${skill.impact_sprite}.png`,
      sprite_scale: 0.8,
      sprite_alpha: 0.8,
      blocks_event_pipeline: false,
    } as SkillEvent_Vfx,
  ],
  on_cast: [
    ...new Array(skill.multihit ?? 1).fill(0).map(
      (_, index) =>
        ({
          __typename: "MeleeHit",
          duration_ticks: 25,
          target: {
            type: "enemy",
            id: 0,
          },
          status_effects: [],
          starting_tick: 0,
          damage: skill.power ?? 10,
          blocks_event_pipeline: false,
        }) as SkillEvent_MeleeHit,
    ),
    ...(skill.cast_sprites ?? []).map(
      (sprite) =>
        ({
          __typename: "Vfx",
          duration_ticks: sprite.duration_ticks ?? 25,
          target: {
            type: sprite.target_type ?? "enemy",
            id: 0,
          },
          status_effects: [],
          starting_tick: 0,
          sprite_path: sprite.sprite_path,
          sprite_scale: sprite.sprite_scale,
          speed: sprite.speed,
          blocks_event_pipeline: sprite.blocks_event_pipeline,
        }) as SkillEvent_Vfx,
    ),
  ],
}));

// Melee Plus Skills - 1 per PokeType
export const [
  BODY_SLAM,
  FIRE_FANG,
  AQUA_CUTTER,
  WOOD_HAMMER,
  THUNDER_FANG,
  ICE_FANG,
  DOUBLE_KICK,
  POISON_FANG,
  EARTH_POWER,
  AIR_SLASH,
  PSYCHIC_FANGS,
  BUG_BITE,
  STONE_AXE,
  SHADOW_CLAWS,
  DRAGON_FANG,
  CRUNCH,
  BEHEMOTH_BASH,
  SPIRIT_BREAK,
]: SkillData[] = [
  {
    id: "body-slam",
    type: PokemonType.NORMAL,
    impact_sprite: "melee-1-white",
    description: "Slams the enemy with the full weight of the body.",
  },
  {
    id: "fire-fang",
    type: PokemonType.FIRE,
    impact_sprite: "melee-1-red",
    hit_cast: {
      sprite_path: "/assets/spells/melee-cast/fire-fang.png",
      sprite_scale: 1.5,
      sprite_alpha: 1,
    },
    momentum: {
      max_stacks: 5,
      effects: [
        {
          type: "cooldown_mult",
          value_per_stack: 0.1,
        },
      ],
    } as SkillMomentum,
    description:
      "Bites with flaming fangs. Reduces 10% cooldown per use, up to 50% reduction.",
  },
  {
    id: "aqua-cutter",
    type: PokemonType.WATER,
    impact_sprite: "melee-1-blue",
    hit_cast: {
      sprite_path: "/assets/spells/melee-cast/slash-blue.png",
      sprite_scale: 1.5,
      sprite_alpha: 1,
    },
    description: "Cuts with a blade of water with high critical hit ratio.",
  },
  {
    id: "wood-hammer",
    type: PokemonType.GRASS,
    impact_sprite: "melee-1-green",
    hit_cast: {
      sprite_path: "/assets/spells/melee-cast/slash-green.png",
      sprite_scale: 1.5,
      sprite_alpha: 1,
    },
    description:
      "Swings a massive wooden hammer at the enemy. 15% bonus damage if there is a FIGHTING-type ally on the team.",
    team_conditions: [
      {
        condition: {
          type: "min_ally_type_count",
          pokemon_type: PokemonType.FIGHTING,
          value: 1,
        },
        effect: {
          stat: "damage_mult",
          value: 1.15,
        },
      },
    ] as SkillData["team_conditions"],
  },
  {
    id: "thunder-fang",
    type: PokemonType.ELECTRIC,
    impact_sprite: "melee-1-yellow",
    hit_cast: {
      sprite_path: "/assets/spells/melee-cast/thunder-fang.png",
      sprite_scale: 1.5,
      sprite_alpha: 1,
    },
    description: "Bites with electrified fangs that may stun or paralyze.",
  },
  {
    id: "ice-fang",
    type: PokemonType.ICE,
    impact_sprite: "melee-1-blue",
    hit_cast: {
      sprite_path: "/assets/spells/melee-cast/ice-fang.png",
      sprite_scale: 1.5,
      sprite_alpha: 1,
    },
    description: "Bites with icy fangs that may freeze the target.",
  },
  {
    id: "double-kick",
    type: PokemonType.FIGHTING,
    impact_sprite: "melee-1-red",
    power: 18,
    multihit: 2,
    hit_cast: {
      sprite_path: "/assets/spells/melee-cast/slash-red.png",
      sprite_scale: 1.5,
      sprite_alpha: 1,
    },
    description: "Delivers two rapid kicks in succession.",
  },
  {
    id: "poison-fang",
    type: PokemonType.POISON,
    impact_sprite: "melee-1-pink",
    hit_cast: {
      sprite_path: "/assets/spells/melee-cast/poison-fang.png",
      sprite_scale: 1.5,
      sprite_alpha: 1,
    },
    description: "Bites with venomous fangs. Applies 🟣POISON to the target.",
    status_effects: [
      { status: StatusEffect.POISON, duration_ticks: 99999, intensity: 1 },
    ],
  },
  {
    id: "earth-power",
    type: PokemonType.GROUND,
    impact_sprite: "melee-1-orange",
    hit_cast: {
      sprite_path: "/assets/spells/melee-cast/slash-orange.png",
      sprite_scale: 1.5,
      sprite_alpha: 1,
    },
    description:
      "Draws power from the earth to attack. If the target is SLOWED, apply STUN for 1.5 seconds.",
    event_checks: [
      {
        __typename: "SkillEventCheck",
        conditions: [
          {
            type: "TARGET_HAS_STATUS",
            status: StatusEffect.SPEED_DOWN,
          },
        ],
        effects: [
          {
            type: "APPLY_STATUS_TARGET",
            status_effects: [
              {
                status: StatusEffect.STUN,
                duration_ticks: msToTicks(1500),
                intensity: 1,
              },
            ],
          },
        ],
      } as SkillEventCheck,
    ] as SkillData["event_checks"],
  },
  {
    id: "air-slash",
    type: PokemonType.FLYING,
    impact_sprite: "melee-1-white",
    hit_cast: {
      sprite_path: "/assets/spells/melee-cast/slash-white.png",
      sprite_scale: 1.5,
      sprite_alpha: 1,
    },
    description: "Cuts with a blade of air.",
  },
  {
    id: "psychic-fangs",
    type: PokemonType.PSYCHIC,
    impact_sprite: "melee-1-pink",
    hit_cast: {
      sprite_path: "/assets/spells/melee-cast/psychic-fangs.png",
      sprite_scale: 1.5,
      sprite_alpha: 1,
    },
    description: "Bites with psychic energy.",
  },
  {
    id: "bug-bite",
    type: PokemonType.BUG,
    impact_sprite: "melee-1-green",
    hit_cast: {
      sprite_path: "/assets/spells/melee-cast/bug-bite.png",
      sprite_scale: 1.5,
      sprite_alpha: 1,
    },
    description: "Bites the enemy with sharp mandibles.",
  },
  {
    id: "stone-axe",
    type: PokemonType.ROCK,
    impact_sprite: "melee-1-white",
    hit_cast: {
      sprite_path: "/assets/spells/melee-cast/slash-orange.png",
      sprite_scale: 1.5,
      sprite_alpha: 1,
    },
    description: "Swings a stone axe that damages the target.",
  },
  {
    id: "shadow-claws",
    power: 6,
    type: PokemonType.GHOST,
    impact_sprite: "melee-1-white",
    multihit: 2,
    description:
      "Sharp metal claws damages the target twice. Reduces cooldown by 10% per use, up to 50% reduction.",
    cast_sprites: [
      {
        duration_ticks: 25,
        target_type: "enemy",
        sprite_path: "/assets/spells/claws/fanfx2_claw_large_black.png",
        sprite_scale: 1,
        speed: 0.75,
        blocks_event_pipeline: false,
      },
      {
        duration_ticks: 35,
        target_type: "enemy",
        sprite_path: "/assets/spells/claws/fx2_claw_large_black.png",
        sprite_scale: 1.25,
        speed: 0.65,
        // Parallel with MeleeHit — do not delay poke/impact.
        blocks_event_pipeline: false,
      },
    ],
    momentum: {
      max_stacks: 5,
      effects: [
        {
          type: "cooldown_mult",
          value_per_stack: 0.1,
        },
      ],
    } as SkillMomentum,
  },
  {
    id: "dragon-fang",
    type: PokemonType.DRAGON,
    impact_sprite: "melee-1-blue",
    hit_cast: {
      sprite_path: "/assets/spells/melee-cast/dragon-fang.png",
      sprite_scale: 1.5,
      sprite_alpha: 1,
    },
    description:
      "Bites with dragon fangs. Reduces cooldown by 10% per use, up to 50% reduction.",
    momentum: {
      max_stacks: 5,
      effects: [
        {
          type: "cooldown_mult",
          value_per_stack: 0.1,
        },
      ],
    } as SkillMomentum,
  },
  {
    id: "crunch",
    type: PokemonType.DARK,
    impact_sprite: "melee-1-white",
    hit_cast: {
      sprite_path: "/assets/spells/melee-cast/crunch.png",
      sprite_scale: 1.5,
      sprite_alpha: 1,
    },
    description:
      "If the target is DEFENSE UP, apply DEFENSE DOWN for 3 seconds and STUN for 1.5 seconds.",
    event_checks: [
      {
        conditions: [
          {
            type: "TARGET_HAS_STATUS",
            status: StatusEffect.DEFENSE_UP,
          },
        ],
        effects: [
          {
            type: "APPLY_STATUS_TARGET",
            status_effects: [
              {
                status: StatusEffect.DEFENSE_DOWN,
                duration_ticks: msToTicks(3000),
                intensity: 2,
              },
              {
                status: StatusEffect.STUN,
                duration_ticks: msToTicks(1500),
                intensity: 1,
              },
            ],
          },
        ],
      },
    ] as SkillData["event_checks"],
  },
  {
    id: "behemoth-bash",
    type: PokemonType.STEEL,
    impact_sprite: "melee-1-white",
    hit_cast: {
      sprite_path: "/assets/spells/melee-cast/slash-white.png",
      sprite_scale: 1.5,
      sprite_alpha: 1,
    },
    description:
      "Delivers a massive bash with overwhelming power. Deals 40% bonus damage if the target is DEFENSE DOWN.",
    event_checks: [
      {
        conditions: [
          {
            type: "TARGET_HAS_STATUS",
            status: StatusEffect.DEFENSE_DOWN,
          },
        ],
        effects: [
          {
            type: "DAMAGE_MULTIPLIER",
            value: 1.4,
          },
        ],
      },
    ] as SkillData["event_checks"],
  },
  {
    id: "spirit-break",
    type: PokemonType.FAIRY,
    impact_sprite: "melee-1-pink",
    hit_cast: {
      sprite_path: "/assets/spells/melee-cast/slash-pink.png",
      sprite_scale: 1.5,
      sprite_alpha: 1,
    },
    event_checks: [
      {
        conditions: [
          {
            type: "TARGET_HAS_STATUS",
            status: StatusEffect.ATTACK_UP,
          },
        ],
        effects: [
          {
            type: "APPLY_STATUS_TARGET",
            status_effects: [
              {
                status: StatusEffect.ATTACK_DOWN,
                duration_ticks: msToTicks(3000),
                intensity: 1,
              },
              {
                status: StatusEffect.STUN,
                duration_ticks: msToTicks(1500),
                intensity: 1,
              },
            ],
          },
        ],
      },
    ] as SkillData["event_checks"],
    description:
      "If the target has ATTACK UP, apply ATTACK DOWN for 3 seconds and STUN for 1.5 seconds.",
  },
].map((skill) => ({
  id: skill.id,
  event_checks:
    "event_checks" in skill
      ? (skill.event_checks as SkillData["event_checks"])
      : undefined,
  momentum:
    "momentum" in skill ? (skill.momentum as SkillData["momentum"]) : undefined,
  team_conditions:
    "team_conditions" in skill
      ? (skill.team_conditions as SkillData["team_conditions"])
      : undefined,
  starts_active:
    "starts_active" in skill ? (skill.starts_active as boolean) : undefined,
  name: parseSkillIdToName(skill.id),
  description: skill.description,
  power: skill.power ?? 13,
  pokemon_type: skill.type,
  types: [SkillType.DAMAGE],
  cooldown_ticks: msToTicks(3000),
  mana_cost: 10,
  range: {
    type: SkillRangeType.POINT_TARGET,
    value: 1,
  },
  on_hit: [
    {
      __typename: "Vfx",
      duration_ticks: 1,
      target: {
        type: "enemy",
        id: 0,
      },
      status_effects: [],
      starting_tick: 0,
      sprite_path: `/assets/spells/impact/${skill.impact_sprite}.png`,
      sprite_scale: 0.8,
      sprite_alpha: 0.8,
      blocks_event_pipeline: false,
    } as SkillEvent_Vfx,
  ],
  on_cast: [
    ...(skill.hit_cast
      ? [
        {
          __typename: "Vfx",
          duration_ticks: 20,
          target: {
            type: "enemy",
            id: 0,
          },
          status_effects: [],
          starting_tick: 0,
          sprite_path: skill.hit_cast.sprite_path,
          sprite_scale: skill.hit_cast.sprite_scale,
          sprite_alpha: skill.hit_cast.sprite_alpha,
          blocks_event_pipeline: false,
        } as SkillEvent_Vfx,
      ]
      : []),
    ...(((skill as { cast_sprites?: Array<{
      duration_ticks?: number;
      target_type?: "enemy" | "self";
      sprite_path: string;
      sprite_scale?: number;
      speed?: number;
      blocks_event_pipeline?: boolean;
    }> }).cast_sprites) ?? []).map(
      (sprite) =>
        ({
          __typename: "Vfx",
          duration_ticks: sprite.duration_ticks ?? 25,
          target: {
            type: sprite.target_type ?? "enemy",
            id: 0,
          },
          status_effects: [],
          starting_tick: 0,
          sprite_path: sprite.sprite_path,
          sprite_scale: sprite.sprite_scale,
          speed: sprite.speed,
          // Always parallel with MeleeHit — same timing model as hit_cast.
          blocks_event_pipeline: false,
        }) as SkillEvent_Vfx,
    ),
    ...(skill.multihit
      ? Array.from({ length: skill.multihit }).map(
        (_, index) =>
          ({
            __typename: "MeleeHit",
            duration_ticks: 50 / skill.multihit,
            target: {
              type: "enemy",
              id: 0,
            },
            status_effects: [],
            starting_tick: 0,
            damage: (skill.power ?? 15) / skill.multihit,
            blocks_event_pipeline: true,
          }) as SkillEvent_MeleeHit,
      )
      : [
        {
          __typename: "MeleeHit",
          duration_ticks: 35,
          target: {
            type: "enemy",
            id: 0,
          },
          status_effects: [],
          starting_tick: 0,
          damage: 10,
          blocks_event_pipeline: true,
        } as SkillEvent_MeleeHit,
      ]),
  ],
}));